import React, { FC } from 'react';
import { t } from 'i18next';
import _ from 'lodash';
import {
  ActionIconsCont,
  Data,
  DeleteParticipantIcon,
  EditParticipantIcon,
  FilledCircle,
  IconImg,
  Language,
  Leader,
  Name,
  NameCont,
  ParticipantsTile,
  Phone,
  Report,
  Role,
  Status,
  StatusIconWrapper,
  Team,
  Tooltip,
  TooltipDescription,
} from './ParticipantsTableTile.styles';
import uk from '../../../assets/icons/flags/uk.svg';
import { useLangManagement } from '../../../hooks/useLangManagement';
import { IParticipantsItem } from '../../../services/surveys';
import CheckIcon from '@/assets/icons/check_participants.svg?react';
import CrossIcon from '@/assets/icons/cross_participants.svg?react';
import EmailIcon from '@/assets/icons/email_participants.svg?react';

interface IParticipantsTableTile {
  item: IParticipantsItem;
  setItemStatus: (id: string) => void;
  deleteItem: (id: string | null) => void;
  editItem: (item: IParticipantsItem) => void;
  isEffectSurvey: boolean;
  isGoodTeamSurvey?: boolean;
  canViewStatus?: boolean;
  departmentName?: string;
}

const defaultFlag = {
  name: 'English',
  shortName: 'en',
  icon: uk,
};

const renderStatusIcon = (code: string, date?: string) => {
  const title = t(`viewSurvey.reportsSetup.status.${code}.title`);
  const description = t(`viewSurvey.reportsSetup.status.${code}.description`, { date });

  return (
    <StatusIconWrapper>
      {code === 'completed' && (
        <FilledCircle color="green">
          <CheckIcon width={25} height={25} />
        </FilledCircle>
      )}
      {code === 'bounced' && (
        <FilledCircle color="red">
          <CrossIcon width={25} height={25} />
        </FilledCircle>
      )}
      {code === 'invited' && <EmailIcon width={25} height={25} />}
      {code === 'uninvited' && <FilledCircle />}

      <Tooltip>
        <strong>{title}</strong>
        <TooltipDescription>{description}</TooltipDescription>
      </Tooltip>
    </StatusIconWrapper>
  );
};

const formatDate = (isoDate: string) => {
  const date = new Date(isoDate);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const ParticipantsTableTile: FC<IParticipantsTableTile> = ({
  item,
  deleteItem,
  editItem,
  isEffectSurvey,
  isGoodTeamSurvey = false,
  canViewStatus = false,
  departmentName,
}) => {
  const { flagsList } = useLangManagement();
  const { id, name, emailAddress, phoneNo, preferredLanguage, team, leader, role, status } = item;
  const { code, datetime } = status;
  const flagObj = flagsList[preferredLanguage as keyof typeof flagsList] || defaultFlag;
  const { flagName, icon } = flagObj;

  const roleLabelKey = (() => {
    const normalizedRole = role?.toLocaleLowerCase();
    if (
      isGoodTeamSurvey &&
      (normalizedRole === 'member' || normalizedRole === 'leader')
    ) {
      return `viewSurvey.reportsSetup.participants.additional.goodTeam${
        normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1)
      }`;
    }
    return `viewSurvey.reportsSetup.participants.additional.${normalizedRole}`;
  })();

  return (
    <ParticipantsTile id="ParticipantsTile">
      <NameCont id="NameCont">
        <Name id="Name">{name}</Name>
        <Data id="Data">{emailAddress}</Data>
      </NameCont>
      <Phone id="Phone">{phoneNo}</Phone>
      <Language id="Language">
        <IconImg src={icon} alt={flagName} />
      </Language>

      {isEffectSurvey ? (
        <>
          <Role id="Role">{t(roleLabelKey)}</Role>
          <Report id="Report">{departmentName}</Report>
        </>
      ) : (
        <>
          <Team id="Team">{team}</Team>
          <Leader id="Leader">{leader}</Leader>
        </>
      )}

      {canViewStatus && (
        <Status data-testid="participantStatus">
          {renderStatusIcon(code, formatDate(datetime))}
        </Status>
      )}

      <ActionIconsCont id="ActionIcons">
        <EditParticipantIcon id="EditParticipantIcon" onClick={() => editItem(item)} />
        <DeleteParticipantIcon id="DeleteParticipantIcon" onClick={() => deleteItem(id)} />
      </ActionIconsCont>
    </ParticipantsTile>
  );
};

export default ParticipantsTableTile;
